import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContentMaxPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContentMaxPriority, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, Loader2, FileText, AlertCircle, X, Image as ImageIcon, Music } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { TIPOS_ANEXO, type TipoAnexo } from './types';
import { useQueryClient } from '@tanstack/react-query';
import { conferirInputFixa } from '@/lib/inputFixaConferencia';
import { Label } from '@/components/ui/label';

interface Props {
  clienteId?: string;
  pedidoId?: string;
  onUploadSuccess?: () => void;
}

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'mp3', 'm4a', 'ogg', 'aac', 'wav', 'opus'];
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'ogg', 'aac', 'wav', 'opus'];
const MAX_SIZE_MB = 50;
const MAX_SIZE_MB_AUDIO = 200;
const IMAGEM_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const TIPO_INPUT_FIXA: TipoAnexo = 'DOCUMENTOS_INPUT_FIXA';

const fmtSize = (bytes: number) =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;

interface ItemSelecionado {
  id: string;
  file: File;
  previewUrl: string | null;
}

export function AnexoUploader({ clienteId, pedidoId, onUploadSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tipoAnexo, setTipoAnexo] = useState<TipoAnexo>('OUTROS');
  const [itens, setItens] = useState<ItemSelecionado[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const { toast } = useToast();
  const qc = useQueryClient();
  const ehInputFixa = tipoAnexo === TIPO_INPUT_FIXA;

  // Revoga os object URLs ao desmontar (via ref p/ pegar o estado atual)
  const itensRef = useRef<ItemSelecionado[]>([]);
  useEffect(() => {
    itensRef.current = itens;
  }, [itens]);
  useEffect(() => {
    return () => {
      itensRef.current.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    };
  }, []);

  const validarArquivo = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      return `formato não permitido`;
    }
    if (ehInputFixa && !IMAGEM_EXTENSIONS.includes(ext)) {
      return 'Documentos de Input Fixa aceita só print em imagem (PNG ou JPG), para a IA ler';
    }
    const limiteMb = AUDIO_EXTENSIONS.includes(ext) ? MAX_SIZE_MB_AUDIO : MAX_SIZE_MB;
    if (file.size > limiteMb * 1024 * 1024) {
      return `muito grande (máx. ${limiteMb}MB)`;
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setErro(null);
    if (!files.length) return;

    const novos: ItemSelecionado[] = [];
    const erros: string[] = [];
    for (const file of files) {
      const err = validarArquivo(file);
      if (err) {
        erros.push(`${file.name}: ${err}`);
        continue;
      }
      const previewUrl =
        file.type.startsWith('image/') || file.type.startsWith('audio/')
          ? URL.createObjectURL(file)
          : null;
      novos.push({ id: crypto.randomUUID(), file, previewUrl });
    }

    if (erros.length) setErro(erros.join(' · '));
    if (novos.length) setItens((prev) => [...prev, ...novos]);
    e.target.value = '';
  };

  const handleRemover = (id: string) => {
    setItens((prev) => {
      const it = prev.find((p) => p.id === id);
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleLimparTudo = () => {
    itens.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    setItens([]);
    setErro(null);
  };

  const handleUpload = async () => {
    if (!itens.length || !tipoAnexo) {
      toast({ variant: 'destructive', title: 'Selecione ao menos um arquivo e o tipo' });
      return;
    }
    if (ehInputFixa) {
      if (!pedidoId) {
        toast({ variant: 'destructive', title: 'Documentos de Input Fixa se anexam dentro do pedido', description: 'Abra o Pedido 360 → Anexos.' });
        return;
      }
      const naoImagem = itens.filter(({ file }) => !IMAGEM_EXTENSIONS.includes(file.name.split('.').pop()?.toLowerCase() || ''));
      if (naoImagem.length) {
        toast({ variant: 'destructive', title: 'Só print em imagem (PNG ou JPG)', description: naoImagem.map((i) => i.file.name).join(' · ') });
        return;
      }
    }

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // cliente_id — resolve uma vez
      let resolvedClienteId = clienteId || null;
      if (!resolvedClienteId && pedidoId) {
        const { data: pedido } = await supabase
          .from('pedidos')
          .select('cliente_id')
          .eq('id', pedidoId)
          .single();
        resolvedClienteId = pedido?.cliente_id || null;
      }

      // CNPJ — resolve uma vez, só se for PRINT_CREDITO
      let cnpjDigits = 'SEM-CNPJ';
      if (tipoAnexo === 'PRINT_CREDITO') {
        try {
          let targetClienteId = clienteId;
          if (!targetClienteId && pedidoId) {
            const { data: pedido } = await supabase
              .from('pedidos')
              .select('cliente_id')
              .eq('id', pedidoId)
              .single();
            targetClienteId = pedido?.cliente_id || undefined;
          }
          if (targetClienteId) {
            const { data: cliente } = await supabase
              .from('clientes')
              .select('cnpj')
              .eq('id', targetClienteId)
              .single();
            if (cliente?.cnpj) {
              cnpjDigits = cliente.cnpj.replace(/\D/g, '') || 'SEM-CNPJ';
            }
          }
        } catch (e) {
          console.warn('Não foi possível buscar CNPJ para nome do print:', e);
        }
      }

      let ok = 0;
      const falhas: string[] = [];

      for (const { file } of itens) {
        try {
          const ext = file.name.split('.').pop()?.toLowerCase();

          let nomeExibicao = file.name;
          let nomeUnico: string;
          if (tipoAnexo === 'PRINT_CREDITO') {
            const dataHoje = new Date().toISOString().slice(0, 10);
            nomeExibicao = `PRINT-CREDITO-${dataHoje}_${cnpjDigits}.${ext}`;
            nomeUnico = `${Date.now()}_${crypto.randomUUID()}_${nomeExibicao}`;
          } else {
            nomeUnico = `${Date.now()}_${crypto.randomUUID()}.${ext}`;
          }

          const pasta = clienteId || pedidoId;
          const caminho = `${pasta}/${nomeUnico}`;

          const { error: uploadError } = await supabase.storage
            .from('anexos-crm')
            .upload(caminho, file, { cacheControl: '3600', upsert: false });
          if (uploadError) throw uploadError;

          const { error: dbError } = await supabase
            .from('anexos_crm')
            .insert({
              cliente_id: resolvedClienteId,
              pedido_id: pedidoId || null,
              nome: nomeExibicao,
              mime: file.type,
              tipo_extensao: ext || 'unknown',
              tamanho: file.size,
              path: caminho,
              tipo_anexo: tipoAnexo,
              usuario_id: user.id,
            });
          if (dbError) throw dbError;

          ok++;
        } catch (e: any) {
          falhas.push(`${file.name}: ${e?.message || e}`);
        }
      }

      const tipoLabel = TIPOS_ANEXO.find((t) => t.value === tipoAnexo)?.label || tipoAnexo;

      if (ok > 0) {
        toast({
          title: `${ok} anexo${ok > 1 ? 's' : ''} enviado${ok > 1 ? 's' : ''}!`,
          description: tipoLabel,
        });
      }
      if (falhas.length) {
        toast({
          variant: 'destructive',
          title: `Falha em ${falhas.length} arquivo${falhas.length > 1 ? 's' : ''}`,
          description: falhas.join(' · '),
        });
      }

      if (ok > 0) {
        handleLimparTudo();
        setTipoAnexo('OUTROS');
        setOpen(false);
        onUploadSuccess?.();
      }

      if (ok > 0 && tipoAnexo === TIPO_INPUT_FIXA && pedidoId) {
        toast({ title: 'A IA está conferindo os prints', description: 'As duas telas do Conexão contra o contrato do pedido.' });
        try {
          const c = await conferirInputFixa(pedidoId, 'importacao');
          toast(c.status === 'aprovado'
            ? { title: 'IA aprovou os documentos do input', description: `Contrato ${c.contratos.join(' / ')}. No envio o CRM confere de novo, sem reler os prints.` }
            : { variant: 'destructive', title: c.status === 'erro' ? 'A IA não conseguiu conferir agora' : 'IA reprovou os documentos do input', description: c.motivos.join(' ') });
        } catch (e) {
          toast({ variant: 'destructive', title: 'A conferência da IA falhou', description: e instanceof Error ? e.message : String(e) });
        } finally {
          qc.invalidateQueries({ queryKey: ['input-fixa-conferencias', pedidoId] });
        }
      }
    } catch (error: any) {
      console.error('Erro ao enviar anexos:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar anexo',
        description: error.message,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default">
          <Upload className="w-4 h-4 mr-2" />
          Enviar Anexo
        </Button>
      </DialogTrigger>
      <DialogContentMaxPriority className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Upload de Anexo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="tipo-anexo">Tipo de Anexo *</Label>
            <Select value={tipoAnexo} onValueChange={(v) => setTipoAnexo(v as TipoAnexo)}>
              <SelectTrigger id="tipo-anexo">
                <SelectValue placeholder="Selecione o tipo..." />
              </SelectTrigger>
              <SelectContentMaxPriority>
                {TIPOS_ANEXO.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContentMaxPriority>
            </Select>
            {ehInputFixa && (
              <p className="text-xs text-muted-foreground">
                Importe dois prints da mesma venda no Conexão, os dois com o número do contrato: o dos Arquivos da Venda e o
                do Histórico de Tratamento com a situação PENDENCIA DE INSTALACAO no topo. A IA lê cada print uma vez; no envio
                para Input realizado Fixa o CRM confere de novo contra o contrato do pedido.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="arquivo">
              Arquivos * <span className="text-xs text-muted-foreground font-normal">(pode selecionar vários)</span>
            </Label>
            <input
              id="arquivo"
              type="file"
              multiple
              accept={ehInputFixa ? '.jpg,.jpeg,.png,image/png,image/jpeg' : ".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp3,.m4a,.ogg,.aac,.wav,.opus,audio/*"}
              onChange={handleFileChange}
              className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
            />
            {erro && (
              <div className="flex items-start gap-2 text-xs text-destructive">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{erro}</span>
              </div>
            )}
          </div>

          {/* Lista dos selecionados */}
          {itens.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Selecionados ({itens.length})</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={handleLimparTudo}
                  className="h-auto py-1 text-xs"
                >
                  <X className="w-3 h-3 mr-1" /> Limpar tudo
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {itens.map(({ id, file, previewUrl }) => {
                  const isImg = file.type.startsWith('image/');
                  const isAudio = file.type.startsWith('audio/');
                  return (
                    <div key={id} className="relative rounded-md border p-2 bg-muted/20 space-y-1">
                      <button
                        type="button"
                        onClick={() => handleRemover(id)}
                        className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full h-5 w-5 grid place-items-center shadow z-10"
                        title="Remover"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {isImg && previewUrl ? (
                        <img src={previewUrl} alt={file.name} className="h-24 w-full object-cover rounded" />
                      ) : isAudio && previewUrl ? (
                        <audio src={previewUrl} controls className="w-full" />
                      ) : (
                        <div className="h-24 grid place-items-center">
                          <FileText className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      <p className="text-xs font-medium truncate" title={file.name}>
                        {file.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{fmtSize(file.size)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Formatos aceitos */}
          {itens.length === 0 && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pt-2 border-t">
              <div className="flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                <span>Imagens: JPG, PNG</span>
              </div>
              <div className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                <span>Documentos: PDF, DOC, DOCX</span>
              </div>
              <div className="flex items-center gap-1">
                <Music className="w-3 h-3" />
                <span>Áudio: MP3, M4A, OGG</span>
              </div>
              <span className="text-muted-foreground/70">• Máx. {MAX_SIZE_MB}MB (áudio {MAX_SIZE_MB_AUDIO}MB)</span>
            </div>
          )}

          <Button
            onClick={handleUpload}
            disabled={uploading || itens.length === 0 || !tipoAnexo}
            className="w-full"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Enviar {itens.length > 0 ? `${itens.length} ` : ''}Anexo{itens.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      </DialogContentMaxPriority>
    </Dialog>
  );
}
